import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { apiClient } from '../../../utils/apiClient';
import TopBar from '../../../layout/TopBar';
import CheckBadge from '../../../macadamia-core/icons/CheckBadge';
import { followUser, unfollowUser, getFollowerCount } from '../../../services/userFollowService';

interface PublicProfileData {
  _id: string;
  firstName: string;
  lastName: string;
  profilePicture?: string;
  bio?: string;
  category?: string;
  subcategory?: string;
  websiteUrl?: string;
  linkedinUrl?: string;
  publicEmail?: string;
  publicProfile: boolean;
  identityVerified?: boolean;
}

interface PublicDossier {
  _id: string;
  title: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
  author: { firstName: string; lastName: string };
}

const CATEGORIES_LABELS: Record<string, string> = {
  AVOCAT: 'Avocat',
  MAGISTRATURE: 'Magistrature',
  JURISTE: 'Juriste',
  UNIVERSITAIRE: 'Universitaire',
  ETUDIANT: 'Étudiant',
  ENTREPRISE: 'Entreprise / Organisation',
};

const PublicProfile = () => {
  const { id } = useParams<{ id: string }>();
  const { user, getAuthorProfilePicture } = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile] = useState<PublicProfileData | null>(null);
  const [dossiers, setDossiers] = useState<PublicDossier[]>([]);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isNotPublic, setIsNotPublic] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [isFollowingYou, setIsFollowingYou] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);
  const [followLoading, setFollowLoading] = useState(false);

  const isOwnProfile = user?.id === id;

  // Charger le profil et les dossiers
  useEffect(() => {
    if (!id) return;

    let isMounted = true;

    const fetchData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const [profileRes, dossiersRes, imgUrl, followerInfo] = await Promise.all([
          apiClient(`/users/${id}/public-profile`),
          apiClient(`/dossiers/user/${id}/public`),
          getAuthorProfilePicture(id, 'avatar'),
          getFollowerCount(id),
        ]);

        if (!isMounted) return;

        if (profileRes.status === 404) {
          const data = await profileRes.json();
          if (data.publicProfile === false) {
            setIsNotPublic(true);
          } else {
            setError('Utilisateur non trouvé');
          }
          setIsLoading(false);
          return;
        }

        if (!profileRes.ok) {
          setError('Erreur lors du chargement du profil');
          setIsLoading(false);
          return;
        }

        const profileData = await profileRes.json();
        setProfile(profileData);

        if (dossiersRes.ok) {
          const dossiersData = await dossiersRes.json();
          setDossiers(dossiersData);
        }

        setImageUrl(imgUrl);
        setIsFollowing(followerInfo.isFollowing);
        setIsFollowingYou(followerInfo.isFollowingYou);
        setFollowerCount(followerInfo.count);
      } catch {
        if (isMounted) setError('Erreur lors du chargement du profil');
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchData();
    return () => { isMounted = false; };
  }, [id, getAuthorProfilePicture]);

  const getCategoryLabel = (key?: string) => {
    if (!key) return null;
    return CATEGORIES_LABELS[key] || key;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const getInitials = () => {
    if (!profile) return '';
    return `${profile.firstName.charAt(0)}${profile.lastName.charAt(0)}`;
  };

  const handleFollow = useCallback(async () => {
    if (!id || followLoading) return;
    setFollowLoading(true);

    const wasFollowing = isFollowing;
    // Mise à jour optimiste
    setIsFollowing(!wasFollowing);
    setFollowerCount((prev) => prev + (wasFollowing ? -1 : 1));

    try {
      const success = wasFollowing
        ? await unfollowUser(id)
        : await followUser(id);

      if (!success) {
        // Rollback
        setIsFollowing(wasFollowing);
        setFollowerCount((prev) => prev + (wasFollowing ? 1 : -1));
      }
    } catch {
      // Rollback
      setIsFollowing(wasFollowing);
      setFollowerCount((prev) => prev + (wasFollowing ? 1 : -1));
    } finally {
      setFollowLoading(false);
    }
  }, [id, isFollowing, followLoading]);

  return (
    <div className="min-h-screen bg-mcdm-primary-200 dark:bg-mcdm-third-500">
      <TopBar />
      <div className="pt-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          {isLoading ? (
            <div className="bg-white dark:bg-mcdm-third-400 p-8 rounded-lg shadow-sm">
              <div className="flex flex-col items-center gap-4">
                <div className="w-24 h-24 rounded-full bg-gray-200 dark:bg-gray-600 animate-pulse" />
                <div className="w-48 h-6 bg-gray-200 dark:bg-gray-600 rounded animate-pulse" />
                <div className="w-32 h-4 bg-gray-200 dark:bg-gray-600 rounded animate-pulse" />
                <div className="w-full h-16 bg-gray-200 dark:bg-gray-600 rounded animate-pulse mt-4" />
              </div>
            </div>
          ) : isNotPublic ? (
            <div className="bg-white dark:bg-mcdm-third-400 p-8 rounded-lg shadow-sm text-center">
              <p className="text-gray-500 dark:text-gray-400 text-lg">Ce profil n'est pas public</p>
              <button
                onClick={() => navigate(-1)}
                className="mt-4 px-4 py-2 text-sm text-mcdm-green-400 hover:underline"
              >
                Retour
              </button>
            </div>
          ) : error ? (
            <div className="bg-white dark:bg-mcdm-third-400 p-8 rounded-lg shadow-sm text-center">
              <p className="text-red-500 text-lg">{error}</p>
              <button
                onClick={() => navigate(-1)}
                className="mt-4 px-4 py-2 text-sm text-mcdm-green-400 hover:underline"
              >
                Retour
              </button>
            </div>
          ) : profile ? (
            <>
              {/* Header du profil */}
              <div className="bg-white dark:bg-mcdm-third-400 p-8 rounded-lg shadow-sm mb-6">
                <div className="flex flex-col items-center gap-4">
                  {/* Photo de profil */}
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={`${profile.firstName} ${profile.lastName}`}
                      className="w-24 h-24 rounded-full object-cover shadow-sm"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-mcdm-third-200 text-white text-2xl flex items-center justify-center shadow-sm">
                      {getInitials()}
                    </div>
                  )}

                  {/* Nom */}
                  <div className="flex items-center gap-1.5">
                    <h1 className="text-xl font-platypi text-mcdm-third-500 dark:text-mcdm-primary-100">
                      {profile.firstName} {profile.lastName}
                    </h1>
                    {profile.identityVerified && (
                      <CheckBadge width="22" height="22" fill="var(--green-400)" className="flex-shrink-0" style={{ marginTop: '3px' }} />
                    )}
                  </div>

                  {/* Catégorie professionnelle */}
                  {profile.category && (
                    <span className="text-sm text-gray-500 dark:text-gray-400 -mt-2">
                      {getCategoryLabel(profile.category)}
                    </span>
                  )}

                  {/* Bouton Suivre + compteur */}
                  <div className="flex items-center gap-3 mt-1">
                    {!isOwnProfile && (
                      <>
                        <button
                          onClick={handleFollow}
                          disabled={followLoading}
                          className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors duration-200 ${
                            isFollowing
                              ? 'bg-mcdm-primary-200 dark:bg-mcdm-third-300 text-mcdm-third-500 dark:text-mcdm-primary-100 hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/20 dark:hover:text-red-400 border border-mcdm-third-200 dark:border-mcdm-third-200'
                              : 'bg-mcdm-green-400 text-white hover:bg-mcdm-green-500'
                          }`}
                        >
                          {isFollowing ? 'Suivi' : 'Suivre'}
                        </button>
                        {isFollowingYou && (
                          <span className="text-xs text-gray-400 dark:text-gray-500 italic">
                            Vous suit
                          </span>
                        )}
                      </>
                    )}
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {followerCount} abonné{followerCount >= 2 ? 's' : ''}
                    </span>
                  </div>

                  {/* Bio */}
                  {profile.bio && (
                    <p className="text-sm text-gray-600 dark:text-gray-300 text-center max-w-md leading-relaxed mt-2">
                      {profile.bio}
                    </p>
                  )}

                  {/* Liens */}
                  <div className="flex flex-wrap gap-3 mt-2">
                    {profile.websiteUrl && (
                      <a
                        href={`https://${profile.websiteUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 text-xs text-mcdm-secondary-400 hover:underline"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
                        </svg>
                        {profile.websiteUrl}
                      </a>
                    )}
                    {profile.linkedinUrl && (
                      <a
                        href={`https://linkedin.com/in/${profile.linkedinUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 text-xs text-mcdm-secondary-400 hover:underline"
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                        </svg>
                        LinkedIn
                      </a>
                    )}
                    {profile.publicEmail && (
                      <a
                        href={`mailto:${profile.publicEmail}`}
                        className="flex items-center gap-1.5 text-xs text-mcdm-secondary-400 hover:underline"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                        </svg>
                        {profile.publicEmail}
                      </a>
                    )}
                  </div>
                </div>
              </div>

              {/* Dossiers publics */}
              <div className="bg-white dark:bg-mcdm-third-400 p-8 rounded-lg shadow-sm">
                <h2 className="text-xl font-platypi text-mcdm-third-500 dark:text-mcdm-primary-100 mb-4">
                  Publications sur Macadamia
                </h2>

                {dossiers.length === 0 ? (
                  <p className="text-sm text-gray-400 dark:text-gray-500 italic">
                    Aucune publication pour le moment
                  </p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {dossiers.map((dossier) => (
                      <button
                        key={dossier._id}
                        onClick={() => navigate(`/dossiers/${dossier._id}`)}
                        className="text-left bg-mcdm-primary-200 dark:bg-mcdm-third-300 rounded-lg shadow-sm hover:shadow-md
                                   transition-all duration-300 p-4 border border-transparent
                                   hover:border-mcdm-secondary-300 dark:hover:border-mcdm-secondary-400"
                      >
                        <h3 className="font-bold text-mcdm-third-500 dark:text-mcdm-primary-100 font-platypi mb-2">
                          {dossier.title || 'Sans titre'}
                        </h3>
                        {dossier.description && (
                          <p className="text-mcdm-third-400 dark:text-mcdm-primary-200 text-sm mb-3">
                            {dossier.description.length > 100
                              ? `${dossier.description.substring(0, 100)}(...)`
                              : dossier.description}
                          </p>
                        )}
                        <div className="text-xs text-mcdm-third-300 dark:text-mcdm-primary-300 mb-3">
                          Mis à jour le {formatDate(dossier.updatedAt)}
                        </div>
                        <div className="text-xs text-mcdm-third-400 dark:text-mcdm-primary-200">
                          Par {dossier.author.firstName} {dossier.author.lastName}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default PublicProfile;
